import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  SimpleGrid,
  Flex,
  Avatar,
  Icon,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaIdBadge, FaUserAlt, FaBuilding } from 'react-icons/fa';
import { PhoneIcon } from '@chakra-ui/icons';

const capitalizeFirst = (text) => {
  if (!text || typeof text !== 'string') return 'N/A';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const ViewUserModal = ({ isOpen, onClose, viewUser }) => {
  const bgHeader = useColorModeValue('blue.600', 'blue.700');
  const modalBg = useColorModeValue('white', 'gray.800');
  const infoBoxBg = useColorModeValue('gray.50', 'gray.700');
  const sectionHeadingColor = useColorModeValue('blue.500', 'blue.300');
  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const valueColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: 'lg' }}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        maxWidth="800px"
        mx="auto"
        rounded="lg"
        shadow="lg"
        overflow="hidden"
      >
        <Box bg={bgHeader} color="white" py={6} textAlign="center">
          <Flex justify="center" mb={4}>
            <Avatar size="2xl" name={viewUser?.name || 'N/A'} />
          </Flex>
          <Text fontSize="2xl" fontWeight="bold">
            {capitalizeFirst(viewUser?.name)}
          </Text>
          <Text color="whiteAlpha.800" fontWeight="semibold" letterSpacing={1}>
            <Icon as={FaIdBadge} mr={1} />
            {capitalizeFirst(viewUser?.role)}
          </Text>
        </Box>

        <ModalBody bg={modalBg} px={6} py={4}>
          <ModalCloseButton />
          <Text
            fontSize="lg"
            fontWeight="bold"
            mb={4}
            color={sectionHeadingColor}
            textAlign="center"
          >
            <Icon as={FaUserAlt} mr={2} />
            User Information
          </Text>
          <Box bg={infoBoxBg} p={4} rounded="md" shadow="sm" mb={6}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color={labelColor}>
                  <Icon as={PhoneIcon} mr={1} />
                  Phone Number:
                </Text>
                <Text color={valueColor}>{viewUser?.phone || 'N/A'}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color={labelColor}>
                  <Icon as={FaUserAlt} mr={1} />
                  Gender:
                </Text>
                <Text color={valueColor}>
                  {capitalizeFirst(viewUser?.gender)}
                </Text>
              </Box>
              {viewUser?.division && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={labelColor}>
                    <Icon as={FaBuilding} mr={1} />
                    Division:
                  </Text>
                  <Text color={valueColor}>
                    {capitalizeFirst(viewUser?.division)}
                  </Text>
                </Box>
              )}
              {viewUser?.district && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={labelColor}>
                    <Icon as={FaBuilding} mr={1} />
                    District:
                  </Text>
                  <Text color={valueColor}>
                    {capitalizeFirst(viewUser?.district)}
                  </Text>
                </Box>
              )}
              {viewUser?.tahsil && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={labelColor}>
                    <Icon as={FaBuilding} mr={1} />
                    Tahsil:
                  </Text>
                  <Text color={valueColor}>
                    {capitalizeFirst(viewUser?.tahsil)}
                  </Text>
                </Box>
              )}
              {Array.isArray(viewUser?.village) &&
                viewUser.village.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color={labelColor}>
                      <Icon as={FaBuilding} mr={1} />
                      Village:
                    </Text>
                    <Tooltip
                      label={viewUser.village.map((v) => v.name).join(', ')}
                    >
                      <Text color={valueColor} isTruncated maxW="full">
                        {viewUser.village
                          .map((v) => capitalizeFirst(v.name))
                          .join(', ')}
                      </Text>
                    </Tooltip>
                  </Box>
                )}
            </SimpleGrid>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ViewUserModal;
